import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import './TravelGallery.css';

const CATEGORIES = ['Red rock & desert', 'Mountains & forests', 'City lights'];
const asset = src => `${process.env.PUBLIC_URL || ''}/${src.replace(/^\/+/, '')}`;
const focusableElements = root => [...root.querySelectorAll('button:not([disabled]), a[href], [tabindex="0"]')]
  .filter(element => !element.closest('[inert], [hidden], [aria-hidden="true"]'));

export default function TravelGallery({photos = [], onClose}) {
  const [category, setCategory] = useState('All photos');
  const [selectedId, setSelectedId] = useState(null);
  const dialogRef = useRef(null);
  const albumRef = useRef(null);
  const viewerRef = useRef(null);
  const returnFocusRef = useRef(null);
  const initialFocusRef = useRef(document.activeElement);
  const swipeRef = useRef(null);
  const filtered = useMemo(() => category === 'All photos' ? photos : photos.filter(photo => photo.category === category), [photos, category]);
  const selectedIndex = filtered.findIndex(photo => photo.id === selectedId);
  const selected = filtered[selectedIndex];
  const viewerOpen = Boolean(selected);

  useEffect(() => {
    const previous = initialFocusRef.current;
    return () => { if (previous?.isConnected) previous.focus(); };
  }, []);

  useLayoutEffect(() => {
    if (viewerOpen) {
      viewerRef.current?.querySelector('button')?.focus();
    } else if (returnFocusRef.current?.isConnected) {
      returnFocusRef.current.focus();
      returnFocusRef.current = null;
    } else {
      albumRef.current?.querySelector('button')?.focus();
    }
  }, [viewerOpen]);

  const closeViewer = () => setSelectedId(null);
  const step = direction => {
    if (filtered.length) setSelectedId(filtered[(selectedIndex + direction + filtered.length) % filtered.length].id);
  };
  const closeActive = () => viewerOpen ? closeViewer() : onClose();
  const handleKeyDown = event => {
    // The room uses global game controls; keystrokes belong to the album while it is open.
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      closeActive();
      return;
    }
    if (viewerOpen && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      if (event.key === 'Home') setSelectedId(filtered[0].id);
      else if (event.key === 'End') setSelectedId(filtered[filtered.length - 1].id);
      else step(event.key === 'ArrowLeft' ? -1 : 1);
      return;
    }
    if (event.key === 'Tab') {
      const activeRoot = viewerOpen ? viewerRef.current : albumRef.current;
      const elements = focusableElements(activeRoot);
      const first = elements[0], last = elements[elements.length - 1];
      if (!elements.length) {
        event.preventDefault();
        dialogRef.current?.focus();
      } else if (event.shiftKey && (document.activeElement === first || !elements.includes(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !elements.includes(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  return <div
    className={`travel-dialog${viewerOpen ? ' travel-dialog--viewing' : ''}`}
    role="dialog"
    aria-modal="true"
    aria-labelledby={viewerOpen ? 'travel-viewer-title' : 'travel-gallery-title'}
    ref={dialogRef}
    tabIndex={-1}
    onKeyDown={handleKeyDown}
    onKeyUp={event => event.stopPropagation()}
    onClick={event => { if (event.target === event.currentTarget) closeActive(); }}
  >
    <section className="travel-album" ref={albumRef} inert={viewerOpen ? '' : undefined} aria-hidden={viewerOpen ? 'true' : undefined}>
      <header className="travel-album-header">
        <div className="travel-album-topline"><span>WORLD 1-1 / THE PERSONAL COLLECTION</span><button className="travel-close" onClick={onClose} aria-label="Close photo album">×</button></div>
        <div className="travel-album-heading"><div><p className="travel-eyebrow">A LITTLE FURTHER FROM THE KEYBOARD</p><h2 id="travel-gallery-title">Life in <em>frames.</em></h2><p className="travel-intro">Open roads, national parks, and moments worth keeping.</p></div><div className="travel-memory-count"><strong>{String(photos.length).padStart(2, '0')}</strong><span>COLLECTED<br/>MEMORIES</span></div></div>
        <div className="travel-filter-row" role="group" aria-label="Filter travel photos">
          {['All photos', ...CATEGORIES.filter(name => photos.some(photo => photo.category === name))].map(name => <button key={name} className="travel-filter" aria-pressed={category === name} onClick={() => setCategory(name)}>{name}<span>{name === 'All photos' ? photos.length : photos.filter(photo => photo.category === name).length}</span></button>)}
        </div>
      </header>
      <div className="travel-album-body">
        <div className="travel-collection-label"><span>{category === 'All photos' ? 'THE TRAVEL JOURNAL' : category.toUpperCase()}</span><span role="status">{filtered.length} {filtered.length === 1 ? 'photo' : 'photos'} · Select to explore</span></div>
        {filtered.length ? <div className="travel-grid">
          {filtered.map(photo => <button
            key={photo.id}
            className={`travel-photo-card${photo.height > photo.width ? ' travel-photo-card--portrait' : ''}`}
            aria-label={`View photo: ${photo.caption}`}
            onClick={event => { returnFocusRef.current = event.currentTarget; setSelectedId(photo.id); }}
          >
            <span className="travel-photo-image"><img src={asset(photo.src)} alt={photo.alt || photo.caption} width={photo.width} height={photo.height} loading="lazy" decoding="async"/><span className="travel-photo-expand" aria-hidden="true">↗</span></span>
            <span className="travel-photo-caption"><span className="travel-photo-category">{photo.category}</span><strong>{photo.caption}</strong><span className="travel-photo-number" aria-hidden="true">{String(photos.indexOf(photo) + 1).padStart(2, '0')}</span></span>
          </button>)}
        </div> : <div className="travel-empty"><span aria-hidden="true">▧</span><h3>The next chapter is on its way.</h3><p>There are no photos in this collection yet.</p></div>}
        <footer className="travel-album-footer"><p>Same curiosity. A different kind of adventure.</p><button onClick={onClose}>← Back to the bonus room</button></footer>
      </div>
    </section>
    {viewerOpen && <section className="travel-viewer" ref={viewerRef} aria-label="Full-size photo">
      <header className="travel-viewer-header"><div><span className="travel-eyebrow">{selected.category}</span><h2 id="travel-viewer-title">{selected.caption}</h2></div><button className="travel-close" onClick={closeViewer} aria-label="Close full-size photo">×</button></header>
      <div className="travel-viewer-stage"
        onTouchStart={event => { const touch = event.touches[0]; swipeRef.current = {x: touch.clientX, y: touch.clientY}; }}
        onTouchEnd={event => {
          const touch = event.changedTouches[0], start = swipeRef.current;
          swipeRef.current = null;
          if (start && Math.abs(touch.clientX - start.x) > 55 && Math.abs(touch.clientY - start.y) < 70) step(touch.clientX < start.x ? 1 : -1);
        }}
        onTouchCancel={() => { swipeRef.current = null; }}
      ><img key={selected.id} className="travel-full-image" src={asset(selected.src)} alt={selected.alt || selected.caption} width={selected.width} height={selected.height} decoding="async"/></div>
      <footer className="travel-viewer-footer"><button className="travel-viewer-nav" onClick={() => step(-1)} aria-label="Previous photo" disabled={filtered.length < 2}>← <span>Previous</span></button><div className="travel-viewer-progress" aria-live="polite" aria-atomic="true"><strong>{String(selectedIndex + 1).padStart(2, '0')} <span>/ {String(filtered.length).padStart(2, '0')}</span></strong><small>ARROWS / SWIPE TO EXPLORE</small></div><button className="travel-viewer-nav" onClick={() => step(1)} aria-label="Next photo" disabled={filtered.length < 2}><span>Next</span> →</button></footer>
    </section>}
  </div>;
}
